// Procedural background music + SFX (Web Audio) and question narration
// (server-side OpenAI TTS played as audio). Used by the TV screen only.
import { API } from "./api";

class AudioEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.sfxGain = null;
    this.muted = localStorage.getItem("gsq_muted") === "1";
    this.musicOn = false;
    this.musicSuppressed = false;
    this.narrationOn = true;
    this.ttsVoice = "nova";
    this.narrator = null;
    this.duckCb = null;
    this.el = null;
    this.baseMusicVolLobby = 0.32;   // pre-game: lively
    this.baseMusicVolRound = 0.18;   // in-round: present background under narration
    this.duckMusicVol = 0.10;        // during narration — softened, never silent
    this.inRound = false;
    this._ducked = false;
    this._pendingNarration = false;
    this._prefetched = null;
    this._narrTimer = null;
    this.playlist = ["/music/party1.mp3", "/music/party2.mp3", "/music/party3.mp3", "/music/party4.mp3"];
    this._order = [];
    this._idx = 0;
  }

  _ensure() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 1;
    this.master.connect(this.ctx.destination);
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = 0.5;
    this.sfxGain.connect(this.master);
  }

  resume() {
    this._ensure();
    if (this.ctx && this.ctx.state === "suspended") this.ctx.resume();
  }

  setMuted(m) {
    this.muted = m;
    localStorage.setItem("gsq_muted", m ? "1" : "0");
    this._ensure();
    if (this.master) this.master.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.05);
    if (this.el) this.el.muted = m;
    if (m) this.stopNarration();
  }

  isMuted() { return this.muted; }

  _note(freq, start, dur, type = "triangle", gain = 0.5) {
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(gain, start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    o.connect(g);
    g.connect(this.sfxGain);
    o.start(start);
    o.stop(start + dur + 0.05);
  }

  // ---- Background music: shuffled playlist of royalty-free tracks (HTMLAudio) ----
  _currentBase() { return this.inRound ? this.baseMusicVolRound : this.baseMusicVolLobby; }

  // Cap music low once the game is underway so narration always cuts through.
  setInRound(on) {
    on = !!on;
    if (this.inRound === on) return;
    this.inRound = on;
    if (this.el && !this._ducked) { try { this.el.volume = this._currentBase(); } catch (e) { /* ignore */ } }
  }

  startMusic() {
    if (this.musicSuppressed) return;
    if (this.musicOn) return;
    this.musicOn = true;
    if (!this.el) {
      this.el = new Audio();
      this.el.volume = this._currentBase();
      this.el.preload = "auto";
      this.el.addEventListener("ended", () => this._nextTrack());
      this.el.addEventListener("error", () => this._nextTrack());
    }
    this.el.volume = this._ducked ? this.duckMusicVol : this._currentBase();
    this.el.muted = this.muted;
    this._order = [...this.playlist].sort(() => Math.random() - 0.5);
    this._idx = 0;
    this._playCurrent();
  }

  _playCurrent() {
    if (!this.el || !this._order.length) return;
    this.el.src = this._order[this._idx];
    const p = this.el.play();
    if (p && p.catch) p.catch(() => {});
  }

  _nextTrack() {
    if (!this.musicOn) return;
    this._idx = (this._idx + 1) % this._order.length;
    if (this._idx === 0) this._order.sort(() => Math.random() - 0.5);
    this._playCurrent();
  }

  stopMusic() {
    this.musicOn = false;
    if (this.el) { this.el.pause(); }
  }

  setMusicSuppressed(on) {
    if (this.musicSuppressed === on) return;
    this.musicSuppressed = on;
    if (on) this.stopMusic();
    else if (!this.muted) this.startMusic();
  }

  // ---- SFX ----
  tick(urgent = false) {
    this._ensure(); if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    // Very light tick — barely-there pacing cue, never prominent.
    this._note(urgent ? 1100 : 820, t, 0.035, "sine", urgent ? 0.07 : 0.035);
  }
  correct() {
    this._ensure(); if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => this._note(f, t + i * 0.08, 0.2, "triangle", 0.5));
  }
  wrong() {
    this._ensure(); if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    this._note(220, t, 0.25, "sawtooth", 0.4);
    this._note(180, t + 0.14, 0.3, "sawtooth", 0.4);
  }
  buzz() {
    this._ensure(); if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    this._note(140, t, 0.35, "square", 0.45);
  }
  reveal() {
    this._ensure(); if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    [392, 523.25].forEach((f, i) => this._note(f, t + i * 0.1, 0.25, "triangle", 0.4));
  }
  fanfare() {
    this._ensure(); if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((f, i) => this._note(f, t + i * 0.12, 0.4, "triangle", 0.5));
  }

  // ---- Narration (server-side OpenAI TTS, reliable on every TV/browser) ----
  setNarrationEnabled(on) { this.narrationOn = on !== false; if (!this.narrationOn) this.stopNarration(); }

  _ensureNarrator() {
    if (this.narrator) return;
    this.narrator = new Audio();
    this.narrator.preload = "auto";
    this.narrator.volume = 1.0;
    this.narrator.addEventListener("ended", () => this._unduckSafe());
    this.narrator.addEventListener("error", () => this._unduckSafe());
  }

  _clearNarrWatchdog() {
    if (this._narrTimer) { clearTimeout(this._narrTimer); this._narrTimer = null; }
  }

  // Always restore the music, even if the narration audio never fires
  // 'ended'/'error' (e.g. the TTS request fails mid-flight). This is the key
  // guard against the music getting stuck ducked/near-silent mid-game.
  _unduckSafe() {
    this._clearNarrWatchdog();
    this._duck(false);
  }

  setDuckCallback(fn) { this.duckCb = fn; }
  _duck(active) {
    // Duck the built-in party music as well as any Spotify playback so the
    // narration is always clearly audible over the top.
    this._ducked = active;
    if (this.el) { try { this.el.volume = active ? this.duckMusicVol : this._currentBase(); } catch (e) { /* ignore */ } }
    if (this.duckCb) { try { this.duckCb(active); } catch (e) { /* ignore */ } }
  }

  unlock() {
    this._ensure();
    this.resume();
    this._ensureNarrator();
    // Prime the narration element within the user gesture so later .play() is allowed.
    try { this.narrator.muted = true; const p = this.narrator.play(); if (p && p.catch) p.catch(() => {}); this.narrator.pause(); this.narrator.muted = false; } catch (e) { /* ignore */ }
  }

  drumroll() {
    this._ensure(); if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    for (let i = 0; i < 12; i++) this._note(85 + i * 5, t + i * 0.045, 0.07, "triangle", 0.22);
  }

  _ttsUrl(text) {
    return `${API}/tts?text=${encodeURIComponent(String(text).slice(0, 800))}&voice=${this.ttsVoice}`;
  }

  // Warm the cache for the NEXT question's narration so playback is instant when
  // it's that question's turn (the /tts response is cacheable for a week).
  prefetchNarration(text) {
    if (!this.narrationOn || !text || text === this._prefetched) return;
    this._prefetched = text;
    try { fetch(this._ttsUrl(text), { cache: "force-cache" }).catch(() => {}); } catch (e) { /* ignore */ }
  }

  narrate(text) {
    if (this.muted || !this.narrationOn || !text) return;
    this._ensureNarrator();
    this._clearNarrWatchdog();
    try {
      this.narrator.pause();
      this.narrator.currentTime = 0;
      this.narrator.src = this._ttsUrl(text);   // served from cache if prefetched -> instant
      this._duck(true);
      const p = this.narrator.play();
      if (p && p.catch) p.catch(() => this._unduckSafe());
      // Hard safety net: if neither playback nor errors resolve, un-duck anyway.
      this._narrTimer = setTimeout(() => this._unduckSafe(), 15000);
      // Tighten the net once we know the clip length.
      const onMeta = () => {
        const d = this.narrator.duration;
        if (isFinite(d) && d > 0) {
          this._clearNarrWatchdog();
          this._narrTimer = setTimeout(() => this._unduckSafe(), d * 1000 + 1500);
        }
      };
      this.narrator.addEventListener("loadedmetadata", onMeta, { once: true });
    } catch (e) { this._unduckSafe(); }
  }

  stopNarration() {
    this._clearNarrWatchdog();
    if (this.narrator) { try { this.narrator.pause(); } catch (e) { /* ignore */ } }
    this._pendingNarration = false;
    this._duck(false);
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  }
}

export const audio = new AudioEngine();
